import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Search,
    Add,
    Edit,
    Security,
    Person,
    AdminPanelSettings,
    PointOfSale,
    Close,
    Lock,
    LockOpen,
    Download,
    Email,
    Phone,
    CalendarToday,
    ViewModule,
    ViewList,
    Visibility
} from '@mui/icons-material';
import AdminSidebar from "../../components/admin/AdminSidebar";
import DashboardHeader from "../../components/admin/DashboardHeader";
import { useAuth } from "../../contexts/AuthContext";
import {
    addUserRole,
    buildUserRoleFormPayload,
    dedupeUserRolesById,
    editUserRole,
    fetchOrganizationUserRoles,
    fetchRolesForInvitation,
    filterUserRolesForOrganization,
    getUserRoleStatusColorClass,
    getUserRoleStatusLabel,
    mapFilterRoleToApiRol,
    mapFilterStatusToApiSts,
    mapUserRoleRecord,
    resolveSolutionId,
} from "../../services/userRoleService";

// Modal invitation membre d'équipe (aligné mobile : téléphone + rôle, sans terminal)
const UserModal = ({ show, onClose, onSave, user, isEdit = false }) => {
    const [phone, setPhone] = useState('243');
    const [roleId, setRoleId] = useState('');
    const [roles, setRoles] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(false);

    useEffect(() => {
        if (!show) return;

        const loadRolesList = async () => {
            setLoadingRoles(true);
            try {
                const list = await fetchRolesForInvitation('');
                setRoles(list);
                if (isEdit && user?.roleId) {
                    setRoleId(user.roleId);
                } else if (list.length === 1) {
                    setRoleId(list[0].id);
                } else if (list.length > 0) {
                    const cashierRole = list.find(
                        (r) =>
                            r.id === 'cashier' ||
                            String(r.name || '').toLowerCase().includes('caiss')
                    );
                    setRoleId(cashierRole?.id || list[0].id);
                }
            } catch (error) {
                console.error('[UserModal] Erreur chargement rôles:', error);
                setRoles([]);
            } finally {
                setLoadingRoles(false);
            }
        };

        loadRolesList();
    }, [show, isEdit, user?.roleId]);

    useEffect(() => {
        if (!show) return;
        if (isEdit && user) {
            const phoneVal = user.phone;
            const phoneStr =
                typeof phoneVal === 'string'
                    ? phoneVal
                    : phoneVal?.fullNumber || phoneVal?.number || '';
            setPhone(phoneStr || '243');
            if (user.roleId) setRoleId(user.roleId);
        } else {
            setPhone('243');
        }
    }, [show, isEdit, user]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const normalized = phone.replace(/\s+/g, '');
        if (normalized.length < 9) {
            alert('Veuillez entrer un numéro de téléphone valide.');
            return;
        }
        if (!roleId) {
            alert('Veuillez sélectionner un rôle.');
            return;
        }

        onSave({
            phone: normalized,
            role_id: roleId,
            userRoleId: user?.userRoleId,
            isEdit,
        });
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-200">
                <div className="flex items-center justify-between p-6 border-b border-slate-200 bg-slate-50/80">
                    <div>
                        <h3 className="text-xl font-bold text-slate-900">
                            {isEdit ? 'Modifier le rôle' : 'Invitation'}
                        </h3>
                        <p className="text-sm text-slate-500 mt-0.5">Compte Netikash existant requis</p>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 hover:bg-slate-200 rounded-xl transition-colors">
                        <Close className="text-slate-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Numéro de téléphone *</label>
                        <input
                            type="tel"
                            required
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="243990000000"
                            maxLength={12}
                        />
                        <p className="mt-1.5 text-xs text-slate-500">Format indicatif 243 (ex. 243990000000)</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Rôle *</label>
                        <select
                            value={roleId}
                            onChange={(e) => setRoleId(e.target.value)}
                            disabled={loadingRoles || roles.length <= 1}
                            className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 disabled:bg-slate-50"
                            required
                        >
                            {loadingRoles ? (
                                <option value="">Chargement des rôles...</option>
                            ) : roles.length === 0 ? (
                                <option value="">Aucun rôle disponible</option>
                            ) : (
                                roles.map((role) => (
                                    <option key={role.id} value={role.id}>
                                        {role.name || role.id}
                                    </option>
                                ))
                            )}
                        </select>
                    </div>

                    <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
                        L&apos;assignation à un terminal se fait dans <strong>Gestion des caisses</strong> lors de la
                        création du POS (comme sur mobile).
                    </p>

                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={loadingRoles || !roleId}
                            className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-2.5 rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600 disabled:opacity-50"
                        >
                            {isEdit ? 'Confirmer' : 'Envoyer l\'invitation'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// Modal de détails utilisateur (données issues de GET /usr/user-roles)
const UserDetailsModal = ({ show, onClose, user, onEdit, onToggleStatus, onResetPassword }) => {
    if (!show || !user) return null;

    const getRoleIcon = (role) => {
        switch (role) {
            case 'admin': return <AdminPanelSettings className="text-purple-500 text-2xl" />;
            case 'caissier': return <PointOfSale className="text-blue-500 text-2xl" />;
            default: return <Person className="text-slate-500 text-2xl" />;
        }
    };

    const getRoleColor = (role) => {
        switch (role) {
            case 'admin': return 'bg-purple-100 text-purple-800 border-purple-200';
            case 'caissier': return 'bg-blue-100 text-blue-800 border-blue-200';
            default: return 'bg-slate-100 text-slate-800 border-slate-200';
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-slate-200">
                    <h3 className="text-xl font-bold text-slate-900">Détails de l'Utilisateur</h3>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                        <Close className="text-slate-500" />
                    </button>
                </div>

                <div className="p-6">
                    {/* En-tête */}
                    <div className="flex items-center space-x-4 mb-6">
                        <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-sm">
                            {getRoleIcon(user.role)}
                        </div>
                        <div className="flex-1">
                            <h4 className="text-2xl font-bold text-slate-900 mb-1">{user.name}</h4>
                            <p className="text-slate-600">{user.email}</p>
                            <div className="flex items-center space-x-3 mt-2">
                                <span className={`px-3 py-1.5 rounded-full text-sm font-medium border ${getRoleColor(user.role)}`}>
                                    {user.roleName || (user.role === 'admin' ? 'Administrateur' : user.role === 'caissier' ? 'Caissier' : user.role)}
                                </span>
                                <span className={`px-3 py-1.5 rounded-full text-sm font-medium border ${getUserRoleStatusColorClass(user.status)}`}>
                                    {user.statusLabel || getUserRoleStatusLabel(user.status)}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {/* Informations de contact */}
                        <div className="bg-slate-50 rounded-2xl p-4">
                            <h5 className="font-semibold text-slate-900 mb-3">Informations de Contact</h5>
                            <div className="space-y-3">
                                <div className="flex items-center space-x-3">
                                    <Email className="text-slate-400" />
                                    <div>
                                        <p className="text-sm text-slate-600">Email</p>
                                        <p className="font-semibold text-slate-900">{user.email}</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <Phone className="text-slate-400" />
                                    <div>
                                        <p className="text-sm text-slate-600">Téléphone</p>
                                        <p className="font-semibold text-slate-900">
                                            {user.phone && typeof user.phone === 'object' ? (user.phone.fullNumber || user.phone.number || '—') : (user.phone || '—')}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <CalendarToday className="text-slate-400" />
                                    <div>
                                        <p className="text-sm text-slate-600">Dernière connexion</p>
                                        <p className="font-semibold text-slate-900">{user.lastLogin}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Informations professionnelles */}
                        <div className="bg-slate-50 rounded-2xl p-4">
                            <h5 className="font-semibold text-slate-900 mb-3">Informations Professionnelles</h5>
                            <div className="space-y-3">
                                <div>
                                    <p className="text-sm text-slate-600">Rôle</p>
                                    <p className="font-semibold text-slate-900">{user.roleName || (user.role === 'admin' ? 'Administrateur' : 'Caissier')}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-slate-600">Statut invitation</p>
                                    <p className="font-semibold text-slate-900">{user.statusLabel || getUserRoleStatusLabel(user.status)}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex space-x-3 pt-4 border-t border-slate-200">
                        <button
                            onClick={() => onEdit(user)}
                            className="flex-1 px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-medium hover:from-amber-600 hover:to-orange-600 transition-all duration-300"
                        >
                            Modifier l'Utilisateur
                        </button>
                        <button
                            onClick={() => onToggleStatus(user)}
                            className={`px-6 py-2.5 rounded-xl font-medium transition-all duration-300 ${
                                user.status === 'active'
                                    ? 'bg-rose-500 text-white hover:bg-rose-600'
                                    : 'bg-emerald-500 text-white hover:bg-emerald-600'
                            }`}
                        >
                            {user.status === 'active' ? 'Désactiver' : 'Activer'}
                        </button>
                        <button
                            onClick={() => onResetPassword(user)}
                            className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-medium hover:from-blue-600 hover:to-cyan-600 transition-all duration-300"
                        >
                            <Lock className="inline mr-2" />
                            Réinitialiser
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Carte utilisateur (vue grille)
const UserCard = ({ user, onEdit, onToggleStatus, onViewDetails, onResetPassword }) => {
    const getRoleIcon = (role) => {
        switch (role) {
            case 'admin': return <AdminPanelSettings className="text-purple-500" fontSize="small" />;
            case 'caissier': return <PointOfSale className="text-blue-500" fontSize="small" />;
            default: return <Person className="text-slate-500" fontSize="small" />;
        }
    };
    const getRoleStyle = (role) => {
        switch (role) {
            case 'admin': return 'bg-purple-50 text-purple-700 border-purple-200';
            case 'caissier': return 'bg-sky-50 text-sky-700 border-sky-200';
            default: return 'bg-slate-100 text-slate-600 border-slate-200';
        }
    };
    const getStatusStyle = (status) =>
        status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200';

    return (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all overflow-hidden flex flex-col">
            <div className="p-5 flex-1">
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center shrink-0 border border-amber-200/50">
                        {getRoleIcon(user.role)}
                    </div>
                    <div className="min-w-0 flex-1">
                        <p
                            className="font-semibold text-slate-900 truncate cursor-pointer hover:text-amber-600"
                            onClick={() => onViewDetails(user)}
                        >
                            {user.name}
                        </p>
                        <p className="text-sm text-slate-500 truncate">{user.email}</p>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium border ${getRoleStyle(user.role)}`}>
                                {user.roleName || (user.role === 'admin' ? 'Admin' : 'Caissier')}
                            </span>
                            <span className={`inline-flex px-2 py-0.5 rounded-lg text-xs font-medium border ${getStatusStyle(user.status)}`}>
                                {user.status === 'active' ? 'Actif' : user.status || '—'}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                        <span className="text-slate-500">Téléphone</span>
                        <span className="text-slate-700 font-medium truncate ml-2">{typeof user.phone === 'string' ? user.phone : (user.phone?.fullNumber || user.phone?.number || '—')}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-slate-500">Statut</span>
                        <span className="text-slate-700 capitalize">{user.status || '—'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-slate-500">Dernière connexion</span>
                        <span className="text-slate-700">{user.lastLogin || '—'}</span>
                    </div>
                </div>
            </div>
            <div className="p-4 pt-0 flex gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={() => onViewDetails(user)}
                    className="flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 py-2 rounded-xl text-sm font-medium bg-amber-50 text-amber-700 border border-amber-200/80 hover:bg-amber-100"
                >
                    <Visibility fontSize="small" />
                    Voir
                </button>
                <button
                    type="button"
                    onClick={() => onEdit(user)}
                    className="inline-flex items-center justify-center p-2 rounded-xl text-slate-600 hover:bg-slate-100"
                    title="Modifier"
                >
                    <Edit fontSize="small" />
                </button>
                <button
                    type="button"
                    onClick={() => onResetPassword(user)}
                    className="inline-flex items-center justify-center p-2 rounded-xl text-slate-600 hover:bg-blue-100 text-blue-600"
                    title="Réinitialiser mot de passe"
                >
                    <Security fontSize="small" />
                </button>
                <button
                    type="button"
                    onClick={() => onToggleStatus(user)}
                    className={`inline-flex items-center justify-center p-2 rounded-xl ${
                        user.status === 'active' ? 'text-rose-600 hover:bg-rose-50' : 'text-emerald-600 hover:bg-emerald-50'
                    }`}
                    title={user.status === 'active' ? 'Désactiver' : 'Activer'}
                >
                    {user.status === 'active' ? <Lock fontSize="small" /> : <LockOpen fontSize="small" />}
                </button>
            </div>
        </div>
    );
};

// Composant Ligne de Tableau pour les Utilisateurs
const UserTableRow = ({ user, onEdit, onToggleStatus, onViewDetails, onResetPassword }) => {
    const getRoleIcon = (role) => {
        switch (role) {
            case 'admin': return <AdminPanelSettings className="text-purple-500 text-sm" />;
            case 'caissier': return <PointOfSale className="text-blue-500 text-sm" />;
            default: return <Person className="text-slate-500 text-sm" />;
        }
    };

    const getRoleColor = (role) => {
        switch (role) {
            case 'admin': return 'bg-purple-100 text-purple-800 border-purple-200';
            case 'caissier': return 'bg-blue-100 text-blue-800 border-blue-200';
            default: return 'bg-slate-100 text-slate-800 border-slate-200';
        }
    };

    const getStatusColor = (status) => {
        return status === 'active'
            ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
            : 'bg-rose-100 text-rose-800 border-rose-200';
    };

    return (
        <tr className="hover:bg-slate-50/50 transition-colors">
            <td className="py-3.5 px-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-200/50 flex items-center justify-center shrink-0">
                        {getRoleIcon(user.role)}
                    </div>
                    <div>
                        <button
                            type="button"
                            onClick={() => onViewDetails(user)}
                            className="font-semibold text-slate-900 hover:text-amber-600 transition-colors text-left"
                        >
                            {user.name}
                        </button>
                        <p className="text-sm text-slate-500">{user.email}</p>
                    </div>
                </div>
            </td>
            <td className="py-3.5 px-4 text-slate-700">{typeof user.phone === 'string' ? user.phone : (user.phone?.fullNumber || user.phone?.number || '—')}</td>
            <td className="py-3.5 px-4">
                <span className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-medium border ${getRoleColor(user.role)}`}>
                    {user.roleName || (user.role === 'admin' ? 'Admin' : 'Caissier')}
                </span>
            </td>
            <td className="py-3.5 px-4 text-slate-600">{user.lastLogin || '—'}</td>
            <td className="py-3.5 px-4">
                <span className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-medium border ${getStatusColor(user.status)}`}>
                    {user.status === 'active' ? 'Actif' : 'Inactif'}
                </span>
            </td>
            <td className="py-3.5 px-4 text-right">
                <div className="flex items-center justify-end gap-1">
                    <button
                        type="button"
                        onClick={() => onViewDetails(user)}
                        className="p-2 rounded-lg text-slate-500 hover:bg-amber-100 hover:text-amber-700 transition-colors"
                        title="Voir"
                    >
                        <Visibility className="text-lg" />
                    </button>
                    <button
                        type="button"
                        onClick={() => onEdit(user)}
                        className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                        title="Modifier"
                    >
                        <Edit className="text-lg" />
                    </button>
                    <button
                        type="button"
                        onClick={() => onResetPassword(user)}
                        className="p-2 rounded-lg text-slate-500 hover:bg-blue-100 hover:text-blue-600 transition-colors"
                        title="Réinitialiser"
                    >
                        <Security className="text-lg" />
                    </button>
                    <button
                        type="button"
                        onClick={() => onToggleStatus(user)}
                        className={`p-2 rounded-lg transition-colors ${
                            user.status === 'active' ? 'text-rose-500 hover:bg-rose-50' : 'text-emerald-500 hover:bg-emerald-50'
                        }`}
                        title={user.status === 'active' ? 'Désactiver' : 'Activer'}
                    >
                        {user.status === 'active' ? <Lock className="text-lg" /> : <LockOpen className="text-lg" />}
                    </button>
                </div>
            </td>
        </tr>
    );
};

const ResetConfirmModal = ({ show, onClose, onConfirm, user }) => {
    if (!show || !user) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                <div className="flex items-center justify-between p-6 border-b border-slate-200">
                    <h3 className="text-xl font-bold text-slate-900">Confirmer la réinitialisation</h3>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                        <Close className="text-slate-500" />
                    </button>
                </div>

                <div className="p-6">
                    <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <Security className="text-blue-500 text-2xl" />
                        </div>
                        <h4 className="text-lg font-semibold text-slate-900 mb-2">
                            Réinitialiser le mot de passe
                        </h4>
                        <p className="text-slate-600">
                            Êtes-vous sûr de vouloir réinitialiser le mot de passe de <strong>{user.name}</strong> ?
                        </p>
                        <p className="text-sm text-slate-500 mt-2">
                            Un email de réinitialisation sera envoyé à {user.email}
                        </p>
                    </div>

                    <div className="flex space-x-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-6 py-2.5 bg-slate-500 text-white rounded-xl font-medium hover:bg-slate-600 transition-all duration-300"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={onConfirm}
                            className="flex-1 px-6 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-medium hover:from-blue-600 hover:to-cyan-600 transition-all duration-300"
                        >
                            Confirmer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
// Composant Principal
const UserManagement = () => {
    const { selectedOrganization } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [timeRange, setTimeRange] = useState('today');
    const [currency, setCurrency] = useState('CDF');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRole, setFilterRole] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showUserModal, setShowUserModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [userToReset, setUserToReset] = useState(null);
    const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'

    // Charger les utilisateurs (même API que mobile : GET /usr/user-roles)
    const loadUsers = useCallback(async () => {
        if (!selectedOrganization?.id) {
            setUsers([]);
            return;
        }

        try {
            setLoading(true);
            const response = await fetchOrganizationUserRoles(selectedOrganization.id, {
                pge: 1,
                lmt: 100,
                sts: mapFilterStatusToApiSts(filterStatus),
                usr: 'all',
                rol: mapFilterRoleToApiRol(filterRole),
            });

            let contents = response.contents || [];
            contents = filterUserRolesForOrganization(contents, selectedOrganization.id);
            contents = dedupeUserRolesById(contents);
            contents = contents.filter((ur) => ur.user != null);

            const formattedUsers = contents.map(mapUserRoleRecord);
            setUsers(formattedUsers);
        } catch (error) {
            console.error('Error loading users:', error);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, [selectedOrganization?.id, filterRole, filterStatus]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    // Ouverture depuis Actions rapides (dashboard) : invitation caissier
    useEffect(() => {
        const state = location.state;
        if (!state?.openInvite) return;

        setSelectedUser(null);
        setIsEditing(false);
        setShowUserModal(true);
        if (state.presetRole === 'caissier' || state.presetRole === 'cashier') {
            setFilterRole('caissier');
        }

        navigate(location.pathname, { replace: true, state: {} });
    }, [location.state, location.pathname, navigate]);

    // Filtrer les utilisateurs (phone peut être string ou objet selon la source)
    const getPhoneDisplay = (p) => (p && typeof p === 'object' && (p.fullNumber ?? p.number)) ? (p.fullNumber || p.number) : (typeof p === 'string' ? p : '');
    const filteredUsers = users.filter(user => {
        const phoneStr = getPhoneDisplay(user.phone);
        const matchesSearch =
            user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            phoneStr.includes(searchTerm);

        return matchesSearch;
    });

    // Gestion des actions
    const handleAddUser = () => {
        setSelectedUser(null);
        setIsEditing(false);
        setShowUserModal(true);
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setIsEditing(true);
        setShowUserModal(true);
    };

    const handleToggleStatus = (user) => {
        setUsers(prev => prev.map(u =>
            u.id === user.id
                ? { ...u, status: u.status === 'active' ? 'inactive' : 'active' }
                : u
        ));
    };

    const handleViewDetails = (user) => {
        setSelectedUser(user);
        setShowDetailsModal(true);
    };

    const handleResetPassword = (user) => {
        setUserToReset(user);
        setShowResetConfirm(true);
    };

    const handleConfirmReset = () => {
        if (userToReset) {
            alert(`Un email de réinitialisation a été envoyé à ${userToReset.email}`);
            console.log(`Réinitialisation du mot de passe pour: ${userToReset.email}`);
        }
        setShowResetConfirm(false);
        setUserToReset(null);
    };

    const handleSaveUser = async (userData) => {
        try {
            if (!selectedOrganization?.id) {
                alert('Aucune organisation sélectionnée.');
                return;
            }

            const normalized = userData.phone.replace(/\s+/g, '');
            if (normalized.length < 9) {
                alert('Numéro de téléphone invalide.');
                return;
            }

            const solutionId = await resolveSolutionId();
            const payload = buildUserRoleFormPayload(selectedOrganization.id, {
                user_id: normalized,
                role_id: userData.role_id,
                solution_id: solutionId,
                userRoleId: userData.isEdit ? selectedUser?.userRoleId : undefined,
            });

            if (userData.isEdit && selectedUser?.userRoleId) {
                await editUserRole(selectedOrganization.id, selectedUser.userRoleId, payload);
            } else {
                await addUserRole(selectedOrganization.id, payload);
            }

            setShowUserModal(false);
            setSelectedUser(null);
            setIsEditing(false);
            await loadUsers();
        } catch (error) {
            console.error('Erreur lors de l\'enregistrement:', error);
            alert(error.message || 'Erreur lors de l\'enregistrement de l\'utilisateur');
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex">
            <AdminSidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                activeSection="users"
            />

            <div className="flex-1 min-h-screen transition-all duration-300 overflow-hidden">
                <div className="min-h-screen p-6">
                    <DashboardHeader
                        timeRange={timeRange}
                        onTimeRangeChange={setTimeRange}
                        currency={currency}
                        onCurrencyChange={setCurrency}
                        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                    />

                    <div className="space-y-6 mt-6">
                        {/* En-tête */}
                        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
                            <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent px-6 py-5">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
                                            <Security className="text-white text-2xl" />
                                        </div>
                                        <div>
                                            <h1 className="text-2xl font-bold text-slate-800">Utilisateurs et rôles</h1>
                                            <p className="text-slate-600 text-sm mt-0.5">Gérez les accès et permissions de votre équipe</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleAddUser}
                                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-all shadow-md hover:shadow-lg"
                                    >
                                        <Add className="text-lg" />
                                        Inviter un utilisateur
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Filtres (style Liens de paiement) */}
                        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                            <div className="flex flex-wrap items-center gap-3">
                                {/* Barre de recherche */}
                                <div className="relative flex-shrink-0">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 text-lg" />
                                    <input
                                        type="text"
                                        placeholder="Rechercher par nom, email, téléphone..."
                                        className="pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200 w-full min-w-[200px] sm:w-64"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>

                                {/* Filtres rôle et statut sur la même ligne */}
                                <div className="flex flex-wrap items-center gap-2">
                                    {['all', 'admin', 'caissier'].map(role => (
                                        <button
                                            key={role}
                                            onClick={() => setFilterRole(role)}
                                            className={`px-4 py-2.5 rounded-xl whitespace-nowrap transition-all duration-200 font-medium ${
                                                filterRole === role
                                                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-sm'
                                                    : 'text-slate-600 hover:text-amber-600 hover:bg-amber-50 border border-slate-200'
                                            }`}
                                        >
                                            {role === 'all' ? 'Tous' : role === 'admin' ? 'Admins' : 'Caissiers'}
                                        </button>
                                    ))}
                                    {[
                                        { id: 'all', label: 'Tous' },
                                        { id: 'active', label: 'Actifs' },
                                        { id: 'declined', label: 'Abandonnés' },
                                        { id: 'revoked', label: 'Révoqués' },
                                    ].map(({ id, label }) => (
                                        <button
                                            key={id}
                                            type="button"
                                            onClick={() => setFilterStatus(id)}
                                            className={`px-4 py-2.5 rounded-xl whitespace-nowrap transition-all duration-200 font-medium ${
                                                filterStatus === id
                                                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-sm'
                                                    : 'text-slate-600 hover:text-amber-600 hover:bg-amber-50 border border-slate-200'
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>

                                {/* Vue cartes / tableau */}
                                <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-xl ml-auto flex-shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('cards')}
                                        className={`p-2 rounded-lg transition-all ${viewMode === 'cards' ? 'bg-white text-amber-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                        title="Vue cartes"
                                    >
                                        <ViewModule className="text-xl" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('table')}
                                        className={`p-2 rounded-lg transition-all ${viewMode === 'table' ? 'bg-white text-amber-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                                        title="Vue tableau"
                                    >
                                        <ViewList className="text-xl" />
                                    </button>
                                </div>

                                <button
                                    type="button"
                                    className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 rounded-xl text-slate-600 hover:text-amber-600 hover:bg-amber-50 transition-all duration-200 font-medium flex-shrink-0"
                                >
                                    <Download fontSize="small" />
                                    Exporter
                                </button>
                            </div>
                        </div>

                        {/* Liste des utilisateurs */}
                        {loading ? (
                            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-12 text-center">
                                <div className="w-12 h-12 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                                <p className="text-slate-600">Chargement des utilisateurs...</p>
                            </div>
                        ) : filteredUsers.length === 0 ? (
                            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-12 sm:p-16 text-center">
                                <div className="w-16 h-16 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center mx-auto mb-5">
                                    <Person className="text-slate-400 text-4xl" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-800 mb-2">
                                    {searchTerm || filterRole !== 'all' || filterStatus !== 'all' ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur'}
                                </h3>
                                <p className="text-slate-600 max-w-sm mx-auto mb-6">
                                    {searchTerm || filterRole !== 'all' || filterStatus !== 'all'
                                        ? 'Aucun utilisateur ne correspond à vos critères.'
                                        : 'Ajoutez votre premier utilisateur pour commencer.'}
                                </p>
                                {(!searchTerm && filterRole === 'all' && filterStatus === 'all') && (
                                    <button
                                        onClick={handleAddUser}
                                        className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-all shadow-md"
                                    >
                                        <Add className="text-lg" />
                                        Inviter un utilisateur
                                    </button>
                                )}
                            </div>
                        ) : viewMode === 'table' ? (
                            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-slate-50 border-b border-slate-200">
                                                <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Utilisateur</th>
                                                <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Téléphone</th>
                                                <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Rôle</th>
                                                <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Dernière connexion</th>
                                                <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Statut</th>
                                                <th className="px-4 py-3.5 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider w-36">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                            {filteredUsers.map(user => (
                                                <UserTableRow
                                                    key={user.id}
                                                    user={user}
                                                    onEdit={handleEditUser}
                                                    onToggleStatus={handleToggleStatus}
                                                    onViewDetails={handleViewDetails}
                                                    onResetPassword={handleResetPassword}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                                {filteredUsers.map(user => (
                                    <UserCard
                                        key={user.id}
                                        user={user}
                                        onEdit={handleEditUser}
                                        onToggleStatus={handleToggleStatus}
                                        onViewDetails={handleViewDetails}
                                        onResetPassword={handleResetPassword}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Modals */}
                    <UserModal
                        show={showUserModal}
                        onClose={() => setShowUserModal(false)}
                        onSave={handleSaveUser}
                        user={selectedUser}
                        isEdit={isEditing}
                    />

                    <UserDetailsModal
                        show={showDetailsModal}
                        onClose={() => setShowDetailsModal(false)}
                        user={selectedUser}
                        onEdit={handleEditUser}
                        onToggleStatus={handleToggleStatus}
                        onResetPassword={handleResetPassword}
                    />

                    <ResetConfirmModal
                        show={showResetConfirm}
                        onClose={() => setShowResetConfirm(false)}
                        onConfirm={handleConfirmReset}
                        user={userToReset}
                    />
                </div>
            </div>
        </div>
    );
};

export default UserManagement;